'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getAllLocums, importLocumCsv, toggleLocumStatus, validateLocumCsv } from 'features/admin/adminApi';

import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import Badge from 'components/base/Badge';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BulkUploadModal from 'components/modals/BulkUploadModal';

import { faFileCsv } from '@fortawesome/free-solid-svg-icons';

import {
  faSearch,
  faUserDoctor,
  faLocationDot,
  faEnvelope,
  faToggleOn,
  faToggleOff,
  faEye,
  faTimes,
  faSpinner,
  faAward,
  faBuildingColumns
} from '@fortawesome/free-solid-svg-icons';

import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';

const ManageLocumPage = () => {
  const navigate = useNavigate();

  const [locums, setLocums] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState({
    active: 0,
    inactive: 0
  });

  const [showUploadModal, setShowUploadModal] = useState(false);

  // FETCH LOCUMS
  const fetchLocums = async (pageNumber = 1) => {
    try {
      setLoading(true);
      setError('');

      const data = await getAllLocums({
        page: pageNumber,
        limit: 10,
        search
      });

      setLocums(data.locums || []);
      setPage(data.page || 1);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // Calculate stats
      const locumsData = data.locums || [];
      setStats({
        active: locumsData.filter((l: any) => l.is_active).length,
        inactive: locumsData.filter((l: any) => !l.is_active).length
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch locums');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocums(1);
  }, []);

  // VIEW DETAILS
  const handleViewDetails = (id: number) => {
    navigate(`/dashboard/admin/user-management/locum/${id}`);
  };

  // TOGGLE STATUS
  const handleToggleStatus = async (id: number) => {
    try {
      setActionLoading(id);
      await toggleLocumStatus(id);
      await fetchLocums(page);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
      console.error('Toggle error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // SEARCH
  const handleSearch = () => {
    fetchLocums(1);
  };

  return (
    <Container className="py-2 py-lg-4" fluid>
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
                Admin Control Center
              </p>
              <h2 className="text-body-emphasis mb-1">Locum Management</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                Monitor, manage, and control locum professional profiles and
                active status.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={6} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Total Locums
                  </p>
                  <h3 className="text-body-emphasis mb-0">{total}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon
                    icon={faUserDoctor}
                    className="text-primary"
                  />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={6} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Active
                  </p>
                  <h3 className="text-success mb-0">{stats.active}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faToggleOn} className="text-success" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={6} lg={4}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Inactive
                  </p>
                  <h3 className="text-danger mb-0">{stats.inactive}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faToggleOff} className="text-danger" />
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert
              variant="danger"
              className="mb-0"
              onClose={() => setError('')}
              dismissible
            >
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search Section */}
      <Row className="mb-4">
        <Col lg={7}>
          <SearchBox
            placeholder="Search by name, email, or location..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSearch()}
          />
        </Col>
        <Col lg={5}>
          <div className="d-flex gap-2 justify-content-end">

            <Button
              variant="phoenix-secondary"
              startIcon={<FontAwesomeIcon icon={faFileCsv} />}
              onClick={() => setShowUploadModal(true)}
            >
              Upload CSV
            </Button>

            <Button
              variant="phoenix-primary"
              onClick={handleSearch}
              startIcon={<FontAwesomeIcon icon={faSearch} />}
            >
              Search
            </Button>

          </div>
        </Col>
      </Row>

      {/* Loading State */}
      {loading && (
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary fw-semibold">
              Loading locum data...
            </p>
          </Col>
        </Row>
      )}

      {/* Table Section */}
      {!loading && (
        <Row>
          <Col>
            <div className="card border-0 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table align-middle mb-0 fs-9">
                  <thead className="bg-body-secondary">
                    <tr>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0">
                        Locum
                      </th>
                      {/* <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Email
                      </th> */}
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Location
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Experience
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Approval
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Status
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {locums.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-6 border-0">
                          <div className="mb-3">
                            <FontAwesomeIcon
                              icon={faUserDoctor}
                              className="text-body-tertiary"
                              size="2x"
                            />
                          </div>
                          <p className="text-body-tertiary fw-semibold mb-0">
                            No locums found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      locums.map(locum => (
                        <tr
                          key={locum.id}
                          className="border-bottom border-translucent hover-row"
                        >
                          <td className="ps-4 pe-4 py-3 border-0">
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-m flex-shrink-0">
                                <span className="avatar-initials rounded-circle bg-info-subtle">
                                  <FontAwesomeIcon
                                    icon={faUserDoctor}
                                    className="text-info"
                                  />
                                </span>
                              </div>
                              <div className="flex-grow-1 min-width-0">
                                <p className="mb-0 fw-semibold text-body-emphasis text-truncate">
                                  {locum.name} {locum.last_name}
                                </p>
                                <p className="mb-0 text-body-tertiary fs-10 text-truncate">
                                  <FontAwesomeIcon
                                    icon={faEnvelope}
                                    className="me-1"
                                  />
                                  {locum.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <a
                              href={`mailto:${locum.email}`}
                              className="text-body text-decoration-none"
                            >
                              {locum.email}
                            </a>
                          </td> */}

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <span className="text-body-emphasis fw-semibold">
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2 text-danger"
                              />
                              {locum.location || 'N/A'}
                            </span>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <span className="text-body d-flex align-items-center justify-content-center">
                              <FontAwesomeIcon
                                icon={faAward}
                                className="me-2 text-warning"
                              />
                              <span className="fw-semibold">
                                {locum.experience_years} Yrs
                              </span>
                            </span>
                          </td>

                          <td className="text-center">
                            <Badge
                              bg={
                                locum.approval_status === "APPROVED"
                                  ? "success"
                                  : locum.approval_status === "REJECTED"
                                    ? "danger"
                                    : "warning"
                              }
                              className="rounded-pill"
                            >
                              {locum.approval_status}
                            </Badge>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <Badge
                              bg={locum.is_active ? 'success' : 'danger'}
                              className="rounded-pill fw-semibold"
                            >
                              {locum.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                size="sm"
                                variant="info"
                                onClick={() => handleViewDetails(locum.id)}
                                startIcon={<FontAwesomeIcon icon={faEye} />}
                                title="View Details"
                              >
                                View
                              </Button>

                              <Button
                                size="sm"
                                variant={
                                  locum.is_active
                                    ? 'subtle-danger'
                                    : 'subtle-success'
                                }
                                onClick={() => handleToggleStatus(locum.id)}
                                disabled={actionLoading === locum.id}
                                startIcon={
                                  actionLoading === locum.id ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={
                                        locum.is_active
                                          ? faToggleOff
                                          : faToggleOn
                                      }
                                    />
                                  )
                                }
                                title={
                                  locum.is_active ? 'Deactivate' : 'Activate'
                                }
                              >
                                {locum.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && locums.length > 0 && (
                <div className="card-footer bg-body border-top border-translucent py-3">
                  <ul className="pagination justify-content-center mb-0 gap-1">
                    {page > 1 && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchLocums(page - 1)}
                        >
                          Previous
                        </button>
                      </li>
                    )}

                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          page === i + 1 ? 'active' : ''
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => fetchLocums(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {page < totalPages && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchLocums(page + 1)}
                        >
                          Next
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}
      <BulkUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        title="Bulk Upload Existing Locums"
        entityName="Locums"
        sampleType="locum"
        validateApi={validateLocumCsv}
        importApi={importLocumCsv}
        onSuccess={() => {
          fetchLocums(page);
        }}
      />

      <style>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s ease;
        }
        .min-width-0 {
          min-width: 0;
        }
      `}</style>
    </Container>
  );
};

export default ManageLocumPage;
